import React, { useMemo } from 'react';

import { ButtonBase, Grid, Typography } from '@mui/material';
import { compareAsc, parseISO } from 'date-fns';
import { sort } from 'ramda';
import { PlainIconButton, PrivateCommentToggle } from 'src/components';
import { useConfirmCommentPrivacyChanges } from 'src/components/comments/commentPrivacyChange';
import { BackArrowIcon } from 'src/components/icons/BackArrowIcon';
import { useEditorTabStateActions } from 'src/editor/editor-tab-state-registry';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { getIsUserInDocOwnerOrg, useGetCurrentDoc } from 'src/hooks/document';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

import { AddReplyForm } from './AddReplyForm';
import { CommentBox } from './CommentBox';
import type { CommentPanelComment } from './CommentsDrawer';

const useStyles = makeStyles<StyleProps>()((theme, { disabled }) => ({
  backButton: {
    margin: theme.spacing(1.5, 0, 1.5, 2),
  },
  commentBar: {
    backgroundColor: theme.palette.common.white,
    borderBottom: `solid 1px ${theme.appColors.rule}`,
    display: 'flex',
    height: 48,
  },
  panelContainer: {
    backgroundColor: theme.appColors.background,
    height: '100%',
    width: '100%',
  },
  resolveButton: {
    padding: theme.spacing(2),
  },
  resolveText: {
    ...theme.appTypography.buttonSmall,
    color: disabled ? theme.appColors.buttonDisabled : theme.appColors.primary,
  },
  subtitle: {
    margin: theme.spacing(1.625, 2, 1.625, 2),
  },
  textAreaContainer: {
    padding: theme.spacing(2),
  },
  threadContainer: {
    flexGrow: 1,
    marginBottom: theme.spacing(1),
    overflowY: 'auto',
  },
}));

interface StyleProps {
  disabled: boolean;
}

interface CommentThreadProps {
  comment: CommentPanelComment;
}

export function CommentThreadPanel({ comment }: CommentThreadProps): JSX.Element {
  const {
    comments: { setActivePanel, setCommentThreadId },
  } = useEditorTabStateActions();
  const { viewing } = useEditingMode();
  const currentUser = useSelectCurrentUser();
  const { document } = useGetCurrentDoc();
  const canCreatePrivateComment = getIsUserInDocOwnerOrg(currentUser, document);
  const canReplyOnThread = !comment.isPrivate || canCreatePrivateComment;
  const { data } = Apollo.useGetCommentRepliesQuery({
    variables: { where: { id: comment.id } },
  });
  const [updateComment] = Apollo.useUpdateCommentMutation();
  const { confirmationDialog, requestThreadPrivacyChange } = useConfirmCommentPrivacyChanges(
    comment.id
  );
  const { classes } = useStyles({ disabled: viewing });

  const repliesSortedByCreatedAt = useMemo(() => {
    const replies = data?.comment?.replies ?? [];

    return sort((a, b) => compareAsc(parseISO(a.createdAt), parseISO(b.createdAt)), replies);
  }, [data?.comment?.replies]);

  return (
    <Grid
      container
      className={classes.panelContainer}
      sx={{
        flexDirection: 'column',
        justifyContent: 'center',
      }}
      wrap="nowrap"
    >
      <div className={classes.commentBar}>
        <PlainIconButton
          Icon={BackArrowIcon}
          className={classes.backButton}
          onClick={(): void => {
            setCommentThreadId(null);
            setActivePanel('ViewComments');
          }}
        />
        <Typography className={classes.subtitle} variant="subtitle1">
          Comment Thread
        </Typography>
      </div>
      {canCreatePrivateComment && (
        <PrivateCommentToggle
          disabled={viewing}
          isPrivate={Boolean(comment.isPrivate)}
          roundedTop={false}
          onChange={requestThreadPrivacyChange}
        />
      )}
      {confirmationDialog}
      <ButtonBase
        className={classes.resolveButton}
        disabled={viewing}
        onClick={async () => {
          await updateComment({
            variables: { input: { id: comment.id, props: { resolved: !comment.resolved } } },
          });
        }}
      >
        <Typography className={classes.resolveText}>
          {comment.resolved ? 'Reopen' : 'Resolve'}
        </Typography>
      </ButtonBase>
      <div className={classes.threadContainer}>
        <CommentBox
          commentOrReply={comment}
          isRootCommentResolved={comment.resolved}
          rootCommentId={comment.id}
          showReplies={false}
        />
        {repliesSortedByCreatedAt.map(
          (reply): JSX.Element => (
            <CommentBox
              key={reply.id}
              commentOrReply={reply}
              isRootCommentResolved={comment.resolved}
              rootCommentId={comment.id}
              // Replies cannot have replies.
              showReplies={false}
              threadIsPrivate={Boolean(comment.isPrivate)}
            />
          )
        )}
      </div>
      <div className={classes.textAreaContainer}>
        <AddReplyForm
          disabled={viewing || comment.resolved || !canReplyOnThread}
          isPrivateThread={comment.isPrivate}
          rootCommentId={comment.id}
        />
      </div>
    </Grid>
  );
}
